// ChatContext.js
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import chatService from '../services/chatService';
import api from '../services/api';

const ChatContext = createContext(null);
const MESSAGES_PAGE_SIZE = 50;
const MIN_REFRESH_GAP_MS = 1200;
const DESKTOP_NOTIF_KEY = 'chat_widget_desktop_notifications';

const notificationApiSupported = typeof window !== 'undefined' && 'Notification' in window;
const readDesktopPreference = () => {
  if (!notificationApiSupported) return false;
  try {
    const stored = localStorage.getItem(DESKTOP_NOTIF_KEY);
    if (stored === null) {
      return Notification.permission === 'granted';
    }
    return stored === '1';
  } catch {
    return Notification.permission === 'granted';
  }
};
const persistDesktopPreference = (value) => {
  if (!notificationApiSupported) return;
  try {
    localStorage.setItem(DESKTOP_NOTIF_KEY, value ? '1' : '0');
  } catch {}
};

const normalizeUnreadMap = (source) => {
  if (!source) return {};
  return Object.entries(source).reduce((acc, [key, value]) => {
    const k = Number(key);
    acc[Number.isNaN(k) ? key : k] = Number(value) || 0;
    return acc;
  }, {});
};
const normId = (id) => { const n = Number(id); return Number.isNaN(n) ? id : n; };

const baseOrigin = (() => {
  const explicitBase = process.env.REACT_APP_WS_BASE_URL || process.env.REACT_APP_API_BASE;
  try {
    if (explicitBase) return new URL(explicitBase, window.location.origin).origin;
    if (api?.defaults?.baseURL) return new URL(api.defaults.baseURL, window.location.origin).origin;
  } catch {}
  return window.location.origin;
})();

const withProto = (url) => {
  try {
    if (!url) return url;
    const fullUrl = new URL(url, baseOrigin);
    return fullUrl.toString();
  } catch (error) {
    console.error('Failed to process URL:', error);
    return url;
  }
};

const buildRoomWebsocketUrl = (roomId) => {
  const url = new URL(`/ws/chat/${roomId}/`, baseOrigin);
  url.protocol = url.protocol.replace('http', 'ws');
  const token = localStorage.getItem('access_token');
  if (token) url.searchParams.set('token', token);
  return url.toString();
};

const buildHubWebsocketUrl = () => {
  const url = new URL('/ws/chat/hub/', baseOrigin);
  url.protocol = url.protocol.replace('http', 'ws');
  const token = localStorage.getItem('access_token');
  if (token) url.searchParams.set('token', token);
  return url.toString();
};

const resolveRoomFolder = (room) => {
  if (!room) return 'projects';
  if (room.folder) return room.folder;
  if (room.room_type === 'direct') return 'personal';
  if (room.is_service || room.is_system_feed) return 'system';
  return 'projects';
};

// Убираем захардкоженный путь к звуку, теперь будем использовать URL из API

export const ChatProvider = ({ children }) => {
  const { user } = useAuth();

  const [rooms, setRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [currentRoomId, setCurrentRoomId] = useState(null);
  const [messagesMap, setMessagesMap] = useState({});
  const [hasMoreByRoom, setHasMoreByRoom] = useState({});
  const [membersMap, setMembersMap] = useState({});
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [widgetOpen, setWidgetOpen] = useState(false);
  const [userSettings, setUserSettings] = useState({ mute_all_notifications: false });
  const [notificationSoundUrl, setNotificationSoundUrl] = useState('');
  const [unreadByRoom, setUnreadByRoom] = useState({});
  const [totalUnread, setTotalUnread] = useState(0);
  const [typingByRoom, setTypingByRoom] = useState({});
  const [desktopNotificationsEnabled, setDesktopNotificationsEnabled] = useState(() => (
    notificationApiSupported &&
    Notification.permission === 'granted' &&
    readDesktopPreference()
  ));
  const [desktopNotificationPermission, setDesktopNotificationPermission] = useState(
    notificationApiSupported ? Notification.permission : 'denied'
  );
  const [desktopNotificationRequesting, setDesktopNotificationRequesting] = useState(false);
  const [popupNotifications, setPopupNotifications] = useState([]);

  const roomSocketRef = useRef(null);
  const hubSocketRef = useRef(null);
  const hubHasConnectedRef = useRef(false);
  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  const userInteractionRef = useRef(false);
  const messagesCursorRef = useRef({});
  const isInitialLoadRef = useRef(false);
  const initializationInFlightRef = useRef(false);
  const currentRoomRef = useRef(null);
  const widgetOpenRef = useRef(false);
  const typingStateRef = useRef({ isTyping: false, timeoutId: null });
  const typingTimeoutsRef = useRef({});
  const prevUnreadByRoomRef = useRef({});
  const roomsRef = useRef([]);
  const roomsReloadInFlightRef = useRef(false);
  const unreadPrimedRef = useRef(false);
  const refreshInFlightRef = useRef(false);
  const lastRefreshAtRef = useRef(0);
  const hubReconnectAttemptRef = useRef(0);
  const hubReconnectTimerRef = useRef(null);
  const popupSeenRef = useRef(new Map());
  const lastSoundUnreadRef = useRef({});
  const messagesInFlightRef = useRef(new Set());
  const membersInFlightRef = useRef(new Set());
  const markReadInFlightRef = useRef(new Set());
  const lastMarkReadAtRef = useRef({});
  const desktopNotificationRequestRef = useRef(false);
  const desktopNotificationPermissionRef = useRef(
    notificationApiSupported ? Notification.permission : 'denied'
  );

  const handlersRef = useRef({
    normalizeMessage: (m) => m,
    isRoomMuted: () => false,
    applyUnreadPatch: () => {},
    applyPinPatch: () => {},
    playNotificationSound: () => {},
    showDesktopNotification: () => {},
    refreshUnreadSafe: () => {},
    setMessagesMap: () => {},
    setRooms: () => {},
    markRoomAsRead: async () => {},
    fetchMembers: async () => {},
    loadMessages: async () => {},
    enqueuePopupNotification: () => {},
    dismissPopupNotification: () => {},
  });

  const shouldDisplayPopup = useCallback((messageId) => {
    if (!messageId) return true;
    const registry = popupSeenRef.current;
    if (registry.has(messageId)) return false;
    registry.set(messageId, Date.now());
    if (registry.size > 200) {
      const oldestKey = registry.keys().next().value;
      if (oldestKey !== undefined) registry.delete(oldestKey);
    }
    return true;
  }, []);

  const shouldPlayUnreadSound = useCallback((roomId, unread) => {
    const rid = normId(roomId);
    if (!rid) return false;
    const value = Math.max(0, Number(unread) || 0);
    if (value === 0) {
      lastSoundUnreadRef.current[rid] = 0;
      return false;
    }
    const prev = Number(lastSoundUnreadRef.current[rid] || 0);
    if (value > prev) {
      lastSoundUnreadRef.current[rid] = value;
      return true;
    }
    return false;
  }, []);

  const isSoundEnabled = useMemo(() => !userSettings?.mute_all_notifications, [userSettings]);
  const isSoundEnabledRef = useRef(true);
  useEffect(() => { isSoundEnabledRef.current = isSoundEnabled; }, [isSoundEnabled]);

  useEffect(() => { currentRoomRef.current = currentRoomId; }, [currentRoomId]);
  useEffect(() => { widgetOpenRef.current = widgetOpen; }, [widgetOpen]);

  useEffect(() => {
    const sum = Object.values(unreadByRoom || {}).reduce((acc, n) => acc + (Number(n) || 0), 0);
    setTotalUnread(sum);
  }, [unreadByRoom]);

  // unlock audio
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const unlock = () => {
      userInteractionRef.current = true;
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (AudioCtx) {
        if (!audioContextRef.current) { try { audioContextRef.current = new AudioCtx(); } catch {} }
        else if (audioContextRef.current.state === 'suspended') audioContextRef.current.resume().catch(() => {});
      }
      if (notificationSoundUrl) {
        try {
          if (!audioRef.current) audioRef.current = new Audio(notificationSoundUrl);
          else if (audioRef.current.src !== notificationSoundUrl) audioRef.current.src = notificationSoundUrl;
          audioRef.current.preload = 'auto'; audioRef.current.setAttribute('playsinline', '');
          const el = audioRef.current, prevMuted = el.muted;
          el.muted = true; el.play().then(() => { el.pause(); el.currentTime = 0; el.muted = prevMuted; }).catch(() => {});
        } catch {}
      }
    };
    window.addEventListener('click', unlock, { passive: true });
    window.addEventListener('keydown', unlock, { passive: true });
    return () => { window.removeEventListener('click', unlock); window.removeEventListener('keydown', unlock); };
  }, [notificationSoundUrl]);

  // Добавляем обработчик для принудительной разблокировки звука
  const forceUnlockAudio = useCallback(() => {
    if (!notificationSoundUrl) return;

    try {
      // Пробуем создать новый AudioContext если еще не создан
      if (!audioContextRef.current) {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (AudioCtx) {
          audioContextRef.current = new AudioCtx();
        }
      }

      // Пробуем разблокировать существующий AudioContext
      if (audioContextRef.current?.state === 'suspended') {
        audioContextRef.current.resume().catch(console.error);
      }

      // Создаем новый элемент Audio если нужно
      if (!audioRef.current) {
        audioRef.current = new Audio(notificationSoundUrl);
      } else if (audioRef.current.src !== notificationSoundUrl) {
        audioRef.current.src = notificationSoundUrl;
      }

      // Настраиваем аудио элемент
      const audio = audioRef.current;
      audio.preload = 'auto';
      audio.volume = 1.0;
      audio.setAttribute('playsinline', '');

      // Пробуем воспроизвести и сразу остановить для разблокировки
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            audio.pause();
            audio.currentTime = 0;
            userInteractionRef.current = true;
          })
          .catch(error => {
            console.warn('Failed to unlock audio:', error);
          });
      }
    } catch (error) {
      console.warn('Error unlocking audio:', error);
    }
  }, [notificationSoundUrl]);

  // Обновляем эффект для автоматической разблокировки звука
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        forceUnlockAudio();
      }
    };

    const handleInteraction = () => {
      forceUnlockAudio();
    };

    window.addEventListener('click', handleInteraction, { once: true });
    window.addEventListener('touchstart', handleInteraction, { once: true });
    window.addEventListener('keydown', handleInteraction, { once: true });
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [forceUnlockAudio]);

  // Обновляем функцию воспроизведения звука
  const playNotificationSound = useCallback(() => {
    if (!isSoundEnabledRef.current || !audioRef.current) {
      console.debug('Sound disabled or audio not initialized');
      return;
    }

    try {
      const audio = audioRef.current;

      // Если звук уже воспроизводится, создаем новый экземпляр
      if (!audio.paused) {
        const newAudio = new Audio(notificationSoundUrl);
        newAudio.volume = 1.0;
        const playPromise = newAudio.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.error('Error playing new notification sound:', error);
            // Пробуем разблокировать звук
            forceUnlockAudio();
          });
        }
        return;
      }

      // Воспроизводим основной звук
      audio.currentTime = 0;
      audio.volume = 1.0;
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.error('Error playing notification sound:', error);
          // Пробуем разблокировать звук
          forceUnlockAudio();
        });
      }
    } catch (error) {
      console.error('Failed to play notification sound:', error);
    }
  }, [notificationSoundUrl, forceUnlockAudio]);

  const enqueuePopupNotification = useCallback((payload) => {
    if (!payload || !payload.id) return;
    setPopupNotifications((prev) => {
      const filtered = prev.filter((item) => item.id !== payload.id);
      const next = [...filtered, payload];
      return next.slice(-3);
    });
  }, []);

  const dismissPopupNotification = useCallback((id) => {
    setPopupNotifications((prev) => prev.filter((item) => item.id !== id));
  }, []);

  // Обновляем обработчики для воспроизведения звука
  useEffect(() => {
    handlersRef.current.playNotificationSound = playNotificationSound;
  }, [playNotificationSound]);

  const showDesktopNotification = useCallback((payload = {}) => {
    if (!notificationApiSupported) return;
    if (!desktopNotificationsEnabled) return;
    if (Notification.permission !== 'granted') return;
    const docVisible = typeof document !== 'undefined' ? document.visibilityState === 'visible' : true;
    if (docVisible && widgetOpenRef.current) return;

    const rid = normId(payload.roomId ?? payload.message?.room ?? payload.message?.room_id);
    const room = roomsRef.current.find((r) => normId(r.id) === rid);
    const title = payload.title || room?.name || 'Новое сообщение';
    const body = payload.body || payload.preview || payload.message?.content?.trim() || 'Новое сообщение';
    if (!body) return;

    try {
      const notification = new Notification(title, {
        body,
        tag: payload.tag || `chat-room-${rid || 'general'}`,
        data: { roomId: rid || null },
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        requireInteraction: true,
        renotify: true,
      });
      notification.onclick = () => {
        try { notification.close(); } catch {}
        if (typeof window !== 'undefined') window.focus();
        setWidgetOpen(true);
        if (notification?.data?.roomId) {
          setCurrentRoomId(notification.data.roomId);
        } else if (rid) {
          setCurrentRoomId(rid);
        }
      };
    } catch (error) {
      console.warn('Failed to show desktop notification:', error);
    }
  }, [desktopNotificationsEnabled, setCurrentRoomId]);

  useEffect(() => {
    handlersRef.current.showDesktopNotification = showDesktopNotification;
  }, [showDesktopNotification]);

  const normalizeMessage = useCallback((message) => {
    if (!message) return message;
    const origin = window.location.origin;
    const normalizeUrl = (url) => (!url ? url : url.startsWith('http') ? url : `${origin}${url}`);
    const attachments = [...(message.attachments || [])]
      .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
      .map((item) => ({ ...item, url: normalizeUrl(item.url) }));
    return {
      ...message,
      event: message.event || message.status || 'created',
      mentions: Array.isArray(message.mentions) ? message.mentions : [],
      attachments,
    };
  }, []);

  const disconnectRoomSocket = useCallback(() => {
    if (roomSocketRef.current) { try { roomSocketRef.current.close(); } catch {} roomSocketRef.current = null; }
  }, []);
  const disconnectHubSocket = useCallback(() => {
    if (hubSocketRef.current) { try { hubSocketRef.current.close(); } catch {} hubSocketRef.current = null; }
  }, []);

  const isRoomMuted = useCallback((roomId) => {
    if (!roomId) return false;
    const rid = normId(roomId);
    const room = rooms.find((r) => normId(r.id) === rid);
    return room?.mute_notifications ?? false;
  }, [rooms]);

  const applyUnreadPatch = useCallback((patch) => {
    if (!patch || typeof patch !== 'object') return;
    const entries = Object.entries(patch).map(([ridRaw, val]) => [normId(ridRaw), Math.max(0, Number(val) || 0)]);
    if (!entries.length) return;

    entries.forEach(([rid, value]) => {
      const prevValue = Number(lastSoundUnreadRef.current[rid] || 0);
      if (value === 0) lastSoundUnreadRef.current[rid] = 0;
      else if (prevValue > value) lastSoundUnreadRef.current[rid] = value;
    });

    setUnreadByRoom((prev) => {
      const next = { ...(prev || {}) };
      entries.forEach(([rid, value]) => { next[rid] = value; });
      const patchMap = Object.fromEntries(entries);
      setRooms((prevRooms) => {
        if (!Array.isArray(prevRooms) || !prevRooms.length) return prevRooms;
        return prevRooms.map((room) => {
          const rid = normId(room.id);
          const unread = patchMap[rid];
          return typeof unread === 'number' ? { ...room, unread_count: unread } : room;
        });
      });
      prevUnreadByRoomRef.current = next;
      return next;
    });

    const knownIds = new Set((roomsRef.current || []).map((room) => normId(room.id)));
    const missing = entries.filter(([rid]) => !knownIds.has(rid)).map(([rid]) => rid);
    if (missing.length && !roomsReloadInFlightRef.current) {
      const refreshFn = handlersRef.current?.refreshRooms;
      if (typeof refreshFn === 'function') {
        roomsReloadInFlightRef.current = true;
        Promise.resolve(refreshFn())
          .catch((error) => console.warn('Chat: auto room refresh failed', error))
          .finally(() => { roomsReloadInFlightRef.current = false; });
      }
    }
  }, []);

  const fetchUnreadNow = useCallback(async () => {
    if (!user) return;
    const { data } = await chatService.fetchUnreadSummary();
    const normalized = normalizeUnreadMap(data.per_room);
    const prevMap = prevUnreadByRoomRef.current || {};
    const socketRoomId = normId(currentRoomRef.current);
    const shouldBeep = [];

    if (unreadPrimedRef.current) {
      Object.keys(normalized).forEach((key) => {
        const rid = normId(key);
        const prev = Number(prevMap[rid] || 0);
        const curr = Number(normalized[rid] || 0);
        if (curr > prev) {
          if (rid === socketRoomId) return;
          if (!handlersRef.current.isRoomMuted(rid) && isSoundEnabledRef.current) shouldBeep.push(rid);
        }
      });
    } else {
      unreadPrimedRef.current = true;
    }

    if (shouldBeep.length > 0) handlersRef.current.playNotificationSound();
    applyUnreadPatch(normalized);
  }, [user, applyUnreadPatch]);

  const refreshUnread = useCallback(async (opts = { force: false }) => {
    const now = Date.now();
    if (!opts.force) {
      if (refreshInFlightRef.current) return;
      if (now - lastRefreshAtRef.current < MIN_REFRESH_GAP_MS) return;
    }
    refreshInFlightRef.current = true;
    try { await fetchUnreadNow(); }
    catch (e) { console.warn('Chat: unread refresh failed', e); }
    finally {
      lastRefreshAtRef.current = Date.now();
      refreshInFlightRef.current = false;
    }
  }, [fetchUnreadNow]);

  const updateUnreadState = useCallback((roomsPayload) => {
    const map = {};
    roomsPayload.forEach((room) => { map[normId(room.id)] = Number(room.unread_count || 0); });
    applyUnreadPatch(map);
  }, [applyUnreadPatch]);

  const applyPinPatch = useCallback((payload) => {
    if (!payload || payload.room_id == null) return;
    const rid = normId(payload.room_id);
    const pinsRaw = Array.isArray(payload.pinned_messages) ? payload.pinned_messages : [];
    const pins = pinsRaw.map((pin) => {
      if (!pin || !pin.message) return pin;
      return { ...pin, message: normalizeMessage(pin.message) };
    });
    setRooms((prev) => {
      if (!Array.isArray(prev) || !prev.length) return prev;
      return prev.map((room) => {
        if (normId(room.id) !== rid) return room;
        const nextPinnedMessage = pins.length ? pins[0]?.message || null : null;
        return {
          ...room,
          pinned_messages: pins,
          pinned_message: nextPinnedMessage,
        };
      });
    });
    if (!pins.length) return;
    setMessagesMap((prev) => {
      const existing = prev?.[rid];
      if (!Array.isArray(existing) || !existing.length) return prev;
      const updated = [...existing];
      const indexMap = new Map(updated.map((msg, index) => [normId(msg.id), index]));
      pins.forEach((pin) => {
        const msg = pin?.message;
        if (!msg) return;
        const idx = indexMap.get(normId(msg.id));
        if (idx == null) return;
        updated[idx] = { ...updated[idx], ...msg };
      });
      return { ...prev, [rid]: updated };
    });
  }, [normalizeMessage]);

  const enhanceRoomPayload = useCallback((room) => {
    if (!room) return room;
    const pinsRaw = Array.isArray(room.pinned_messages) ? room.pinned_messages : [];
    const pins = pinsRaw.map((pin) => {
      if (!pin || !pin.message) return pin;
      return { ...pin, message: normalizeMessage(pin.message) };
    });
    const firstPinMessage = pins.length ? pins[0]?.message || null : null;
    return {
      ...room,
      pinned_messages: pins,
      pinned_message: firstPinMessage || room.pinned_message || null,
    };
  }, [normalizeMessage]);

  const loadRooms = useCallback(async () => {
    if (!user) return;
    try {
      setRoomsLoading(true);
      const { data } = await chatService.fetchChatRooms();
      const list = (data.rooms || []).map(enhanceRoomPayload);
      // unread state is applied immediately, before React commits setRooms.
      // Keep the ref in sync first so it does not treat every loaded room as new
      // and start a duplicate rooms request.
      roomsRef.current = list;
      setRooms(list);
      updateUnreadState(list);
      if (list.length) {
        setCurrentRoomId((current) => current || normId(list[0].id));
      }
    } catch (error) {
      console.error('Chat: failed to load rooms', error);
    } finally {
      setRoomsLoading(false);
    }
  }, [user, updateUnreadState, enhanceRoomPayload]);

  const searchDirectoryUsers = useCallback(async (query = '') => {
    try {
      const params = {};
      if (query) params.q = query;
      const { data } = await chatService.searchChatUsers(params);
      return data.users || [];
    } catch (error) {
      console.error('Chat: failed to search users', error);
      throw error;
    }
  }, []);

  const startDirectConversation = useCallback(async (targetUserId) => {
    try {
      const { data } = await chatService.startDirectChat(targetUserId);
      const roomPayload = enhanceRoomPayload(data);
      setRooms((prev) => {
        const idx = prev.findIndex((room) => normId(room.id) === normId(roomPayload.id));
        if (idx !== -1) {
          const clone = [...prev];
          clone[idx] = { ...prev[idx], ...roomPayload };
          return clone;
        }
        return [...prev, roomPayload];
      });
      applyUnreadPatch({ [normId(roomPayload.id)]: Number(roomPayload.unread_count || 0) });
      setCurrentRoomId(roomPayload.id);
      return roomPayload;
    } catch (error) {
      console.error('Chat: failed to start direct chat', error);
      throw error;
    }
  }, [enhanceRoomPayload, applyUnreadPatch, setCurrentRoomId]);

  const fetchNotificationSound = useCallback(async () => {
    try {
      const { data } = await chatService.fetchNotificationSettings();
      if (data.notification_sound_url) setNotificationSoundUrl(withProto(data.notification_sound_url));
    } catch (error) {
      console.warn('Chat: failed to load notification settings', error);
    }
  }, []);

  const loadUserSettings = useCallback(async () => {
    try {
      const { data } = await chatService.fetchChatUserSettings();
      setUserSettings(data);
    } catch (error) {
      console.warn('Chat: failed to load user settings', error);
    }
  }, []);

  useEffect(() => {
    if (!notificationSoundUrl || !userInteractionRef.current) return;
    try {
      if (!audioRef.current) audioRef.current = new Audio(notificationSoundUrl);
      else if (audioRef.current.src !== notificationSoundUrl) audioRef.current.src = notificationSoundUrl;
      audioRef.current.preload = 'auto';
      audioRef.current.setAttribute('playsinline', '');
      const el = audioRef.current;
      const prevMuted = el.muted;
      el.muted = true;
      el.play().then(() => {
        el.pause();
        el.currentTime = 0;
        el.muted = prevMuted;
      }).catch((error) => {
        console.warn('Failed to preload notification sound:', error);
      });
    } catch (error) {
      console.warn('Error setting up notification sound:', error);
    }
  }, [notificationSoundUrl]);

  // messages/members
  const loadMessagesCb = useCallback(async (roomId, options = {}) => {
    if (!roomId) return;
    const rid = normId(roomId);
    const requestKey = `${rid}:${options.beforeId || (options.append ? 'append' : 'latest')}`;
    if (messagesInFlightRef.current.has(requestKey)) return;
    messagesInFlightRef.current.add(requestKey);
    const params = { limit: MESSAGES_PAGE_SIZE + 1 };

    if (options.beforeId) params.before_id = options.beforeId;
    else if (options.append) {
      const cursor = messagesCursorRef.current[rid];
      if (cursor) params.before_id = cursor;
    }

    try {
      setMessagesLoading(true);
      const { data } = await chatService.fetchRoomMessages(rid, params);
      const incomingRaw = (data.messages || []).map(handlersRef.current.normalizeMessage);
      const hasMore = incomingRaw.length > MESSAGES_PAGE_SIZE;
      const incoming = hasMore ? incomingRaw.slice(1) : incomingRaw;
      setHasMoreByRoom((prev) => ({ ...prev, [rid]: hasMore }));
      handlersRef.current.setMessagesMap((prev) => {
        const existing = prev[rid] || [];
        const merged = options.append ? [...incoming, ...existing] : incoming;
        return { ...prev, [rid]: merged };
      });

      if (incoming.length > 0) messagesCursorRef.current[rid] = incoming[0].id;
      else messagesCursorRef.current[rid] = null;
    } catch (error) {
      console.error('Chat: failed to load messages', error);
    } finally {
      messagesInFlightRef.current.delete(requestKey);
      setMessagesLoading(false);
    }
  }, []);
  const loadMessagesRef = useRef(loadMessagesCb);
  useEffect(() => { loadMessagesRef.current = loadMessagesCb; }, [loadMessagesCb]);

  const fetchMembersCb = useCallback(async (roomId) => {
    if (!roomId) return;
    const rid = normId(roomId);
    if (membersMap[rid]) return;
    if (membersInFlightRef.current.has(rid)) return;
    membersInFlightRef.current.add(rid);
    try {
      const { data } = await chatService.fetchRoomMembers(rid);
      setMembersMap((prev) => ({ ...prev, [rid]: data.members || [] }));
    } catch (error) {
      console.warn('Chat: failed to load members', error);
    } finally {
      membersInFlightRef.current.delete(rid);
    }
  }, [membersMap]);
  const fetchMembersRef = useRef(fetchMembersCb);
  useEffect(() => { fetchMembersRef.current = fetchMembersCb; }, [fetchMembersCb]);

  const markRoomAsReadCb = useCallback(async (roomId) => {
    if (!roomId) return;
    const rid = normId(roomId);
    const now = Date.now();
    if (markReadInFlightRef.current.has(rid) || now - (lastMarkReadAtRef.current[rid] || 0) < 2000) return;
    markReadInFlightRef.current.add(rid);
    lastMarkReadAtRef.current[rid] = now;
    try {
      await chatService.markRoomAsRead(rid);
      applyUnreadPatch({ [rid]: 0 });
      setRooms((prev) => prev.map((room) => (normId(room.id) === rid ? { ...room, unread_count: 0 } : room)));
    } catch (error) {
      console.warn('Chat: failed to mark as read', error);
    } finally {
      markReadInFlightRef.current.delete(rid);
    }
  }, [applyUnreadPatch]);
  const markRoomAsReadRef = useRef(markRoomAsReadCb);
  useEffect(() => { markRoomAsReadRef.current = markRoomAsReadCb; }, [markRoomAsReadCb]);

  const handleUpdateMessage = useCallback(async (roomId, messageId, payload) => {
    if (!roomId || !messageId) return null;
    const rid = normId(roomId);
    try {
      const response = await chatService.updateRoomMessage(rid, messageId, payload);
      const message = handlersRef.current.normalizeMessage(response.data);
      handlersRef.current.setMessagesMap((prev) => {
        const existing = prev[rid] || [];
        const updated = existing.map((it) => (it.id === message.id ? { ...it, ...message } : it));
        return { ...prev, [rid]: updated };
      });
      return message;
    } catch (error) {
      console.error('Chat: failed to update message', error);
      throw error;
    }
  }, []);

  const handleDeleteMessage = useCallback(async (roomId, messageId) => {
    if (!roomId || !messageId) return null;
    const rid = normId(roomId);
    try {
      const { data } = await chatService.deleteRoomMessage(rid, messageId);
      const message = handlersRef.current.normalizeMessage(data);
      handlersRef.current.setMessagesMap((prev) => {
        const existing = prev[rid] || [];
        const updated = existing.map((it) => (it.id === messageId ? { ...it, ...message, is_deleted: true } : it));
        return { ...prev, [rid]: updated };
      });
      return message;
    } catch (error) {
      console.error('Chat: failed to delete message', error);
      throw error;
    }
  }, []);

  const handleToggleMute = useCallback(async (roomId, mute) => {
    const rid = normId(roomId);
    try {
      const { data } = await chatService.toggleRoomMute(rid, mute);
      setRooms((prev) => prev.map((room) => (normId(room.id) === rid ? { ...room, mute_notifications: data.mute_notifications } : room)));
      return data.mute_notifications;
    } catch (error) {
      console.error('Chat: failed to toggle mute', error);
      throw error;
    }
  }, []);

  const handleToggleGlobalSound = useCallback(async (muteAll) => {
    setUserSettings((prev) => ({ ...prev, mute_all_notifications: muteAll }));
    try {
      const { data } = await chatService.updateChatUserSettings({ mute_all_notifications: muteAll });
      if (typeof data?.mute_all_notifications === 'boolean') {
        setUserSettings((prev) => ({ ...prev, mute_all_notifications: data.mute_all_notifications }));
      }
      if (!data.mute_all_notifications && audioContextRef.current && audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume().catch(() => {});
      }
    } catch (error) {
      console.error('Chat: failed to update user settings', error);
      setUserSettings((prev) => ({ ...prev, mute_all_notifications: !muteAll }));
      try { alert('Не удалось обновить настройку звука. Проверьте сеть или авторизацию.'); } catch {}
      throw error;
    }
  }, []);

  const syncDesktopNotificationState = useCallback(() => {
    if (!notificationApiSupported) return;
    const permission = Notification.permission;
    const previousPermission = desktopNotificationPermissionRef.current;
    desktopNotificationPermissionRef.current = permission;
    setDesktopNotificationPermission(permission);
    if (permission === 'granted') {
      const shouldEnable = previousPermission !== 'granted' || readDesktopPreference();
      setDesktopNotificationsEnabled(shouldEnable);
      if (shouldEnable) persistDesktopPreference(true);
    } else {
      setDesktopNotificationsEnabled(false);
    }
  }, []);

  const toggleDesktopNotifications = useCallback(async () => {
    if (!notificationApiSupported) {
      try { alert('Браузер не поддерживает уведомления на компьютере.'); } catch {}
      return;
    }

    if (desktopNotificationRequestRef.current) return;

    const permission = Notification.permission;
    desktopNotificationPermissionRef.current = permission;
    setDesktopNotificationPermission(permission);
    if (permission === 'granted') {
      const next = !desktopNotificationsEnabled;
      setDesktopNotificationsEnabled(next);
      persistDesktopPreference(next);
      if (next) {
        try {
          new Notification('Уведомления включены', {
            body: 'Теперь вы будете получать уведомления даже при закрытом виджете.',
            tag: 'chat-widget-enabled',
          });
        } catch {}
      }
      return;
    }

    if (permission === 'denied') {
      try {
        alert('Уведомления заблокированы браузером. Нажмите на значок настроек слева от адреса сайта, разрешите уведомления и вернитесь на эту вкладку. Перезагрузка страницы не нужна.');
      } catch {}
      setDesktopNotificationPermission('denied');
      setDesktopNotificationsEnabled(false);
      return;
    }

    desktopNotificationRequestRef.current = true;
    setDesktopNotificationRequesting(true);
    try {
      const result = await Notification.requestPermission();
      desktopNotificationPermissionRef.current = result;
      setDesktopNotificationPermission(result);
      if (result === 'granted') {
        setDesktopNotificationsEnabled(true);
        persistDesktopPreference(true);
        try {
          new Notification('Уведомления включены', {
            body: 'Теперь уведомления будут приходить через системную панель.',
            tag: 'chat-widget-enabled',
          });
        } catch {}
      } else {
        setDesktopNotificationsEnabled(false);
        if (result === 'default') {
          try {
            alert('Браузер не получил ответ. Нажмите «Включить» ещё раз и подтвердите разрешение в появившемся окне.');
          } catch {}
        }
      }
    } catch (error) {
      console.warn('Failed to request notification permission:', error);
      syncDesktopNotificationState();
      try { alert('Не удалось запросить разрешение на уведомления. Проверьте настройки сайта в браузере.'); } catch {}
    } finally {
      desktopNotificationRequestRef.current = false;
      setDesktopNotificationRequesting(false);
    }
  }, [desktopNotificationsEnabled, syncDesktopNotificationState]);

  const applyMessageReactionPatch = useCallback((rawMessage) => {
    if (!rawMessage) return;
    const normalizedMessage = normalizeMessage(rawMessage);
    const rid = normId(normalizedMessage.room || normalizedMessage.room_id || currentRoomRef.current);
    if (!rid) return;

    setMessagesMap((prev) => {
      const existing = prev[rid] || [];
      const idx = existing.findIndex((item) => normId(item.id) === normId(normalizedMessage.id));
      if (idx === -1) {
        return { ...prev, [rid]: [...existing, normalizedMessage] };
      }
      const updated = [...existing];
      updated[idx] = { ...existing[idx], ...normalizedMessage };
      return { ...prev, [rid]: updated };
    });

    setRooms((prevRooms) => {
      if (!Array.isArray(prevRooms) || !prevRooms.length) return prevRooms;
      return prevRooms.map((room) => {
        if (normId(room.id) !== rid) return room;
        const lastMsg = room.last_message;
        if (!lastMsg || normId(lastMsg.id) !== normId(normalizedMessage.id)) return room;
        return {
          ...room,
          last_message: {
            ...lastMsg,
            reactions: normalizedMessage.reactions,
          },
        };
      });
    });
  }, [normalizeMessage]);

  // === Методы для pin/unpin и emoji-реакций ===
  const pinMessage = async (roomId, messageId) => {
    try {
      const response = await chatService.pinMessage(roomId, messageId);
      if (response?.data) applyPinPatch(response.data);
    } catch (error) {
      console.error('Failed to pin message', error);
    }
  };
  const unpinMessage = async (roomId, messageId) => {
    try {
      const response = await chatService.unpinMessage(roomId, messageId);
      if (response?.data) applyPinPatch(response.data);
    } catch (error) {
      console.error('Failed to unpin message', error);
    }
  };
  const addReaction = async (roomId, messageId, emoji) => {
    try {
      const response = await chatService.addReaction(roomId, messageId, emoji);
      if (response?.data) applyMessageReactionPatch(response.data);
    } catch (error) {
      console.error('Failed to add reaction', error);
      throw error;
    }
  };
  const removeReaction = async (roomId, messageId, emoji) => {
    try {
      const response = await chatService.removeReaction(roomId, messageId, emoji);
      if (response?.data) applyMessageReactionPatch(response.data);
    } catch (error) {
      console.error('Failed to remove reaction', error);
      throw error;
    }
  };

  useEffect(() => {
    handlersRef.current.normalizeMessage = normalizeMessage;
    handlersRef.current.isRoomMuted = isRoomMuted;
    handlersRef.current.applyUnreadPatch = applyUnreadPatch;
    handlersRef.current.applyPinPatch = applyPinPatch;
    handlersRef.current.playNotificationSound = playNotificationSound;
    handlersRef.current.refreshUnreadSafe = refreshUnread;
    handlersRef.current.refreshRooms = loadRooms;
    handlersRef.current.setMessagesMap = setMessagesMap;
    handlersRef.current.setRooms = setRooms;
    handlersRef.current.markRoomAsRead = (rid) => markRoomAsReadRef.current?.(rid);
    handlersRef.current.fetchMembers = (rid) => fetchMembersRef.current?.(rid);
    handlersRef.current.loadMessages = (rid, opts) => loadMessagesRef.current?.(rid, opts);
    handlersRef.current.enqueuePopupNotification = enqueuePopupNotification;
    handlersRef.current.dismissPopupNotification = dismissPopupNotification;
  }, [normalizeMessage, isRoomMuted, applyUnreadPatch, applyPinPatch, playNotificationSound, refreshUnread, loadRooms, enqueuePopupNotification, dismissPopupNotification]);

  // INIT
  useEffect(() => {
    if (!user) {
      setRooms([]); setMessagesMap({}); setMembersMap({});
      setHasMoreByRoom({});
      setUnreadByRoom({}); setTotalUnread(0);
      disconnectRoomSocket(); disconnectHubSocket();
      isInitialLoadRef.current = false;
      initializationInFlightRef.current = false;
      prevUnreadByRoomRef.current = {};
      unreadPrimedRef.current = false;
      return;
    }

    if (!isInitialLoadRef.current && !initializationInFlightRef.current) {
      initializationInFlightRef.current = true;
      (async () => {
        try {
          await Promise.all([loadRooms(), loadUserSettings(), fetchNotificationSound()]);
          await refreshUnread({ force: true });
          isInitialLoadRef.current = true;
        } finally {
          initializationInFlightRef.current = false;
        }
      })();
    }
  }, [user, loadRooms, loadUserSettings, fetchNotificationSound, disconnectRoomSocket, disconnectHubSocket, refreshUnread]);

  useEffect(() => {
    roomsRef.current = rooms || [];
  }, [rooms]);

  // на открытие виджета/переключение комнаты
  useEffect(() => {
    if (!widgetOpen || !currentRoomId) return;
    const rid = normId(currentRoomId);
    handlersRef.current.fetchMembers(rid);
    handlersRef.current.loadMessages(rid);
    handlersRef.current.markRoomAsRead(rid);
  }, [widgetOpen, currentRoomId]);

  useEffect(() => {
    if (!notificationApiSupported) return undefined;

    const syncWhenVisible = () => {
      if (document.visibilityState === 'visible') syncDesktopNotificationState();
    };

    syncDesktopNotificationState();
    window.addEventListener('focus', syncDesktopNotificationState);
    window.addEventListener('pageshow', syncDesktopNotificationState);
    document.addEventListener('visibilitychange', syncWhenVisible);

    return () => {
      window.removeEventListener('focus', syncDesktopNotificationState);
      window.removeEventListener('pageshow', syncDesktopNotificationState);
      document.removeEventListener('visibilitychange', syncWhenVisible);
    };
  }, [syncDesktopNotificationState]);

  // --- ДОБАВЛЕНО: определение handleSocketMessage вне useEffect ---
  const handleSocketMessage = (event) => {
    const currentRid = normId(currentRoomId);
    const socketData = JSON.parse(event.data);
    if (socketData.type === 'reaction' && socketData.payload) {
      const message = handlersRef.current.normalizeMessage(socketData.payload);
      const targetRoomId = normId(message.room || currentRid);
      handlersRef.current.setMessagesMap((prev) => {
        const existing = prev[targetRoomId] || [];
        const idx = existing.findIndex((item) => normId(item.id) === normId(message.id));
        if (idx === -1) {
          return { ...prev, [targetRoomId]: [...existing, message] };
        }
        const updated = [...existing];
        updated[idx] = { ...existing[idx], ...message };
        return { ...prev, [targetRoomId]: updated };
      });
      return;
    }
    if (socketData.type === 'pinned' && socketData.payload) {
      handlersRef.current.applyPinPatch(socketData.payload);
      return;
    }
    if (socketData.type === 'unpinned' && socketData.payload) {
      handlersRef.current.applyPinPatch(socketData.payload);
      return;
    }

    try {
      if (socketData.type === 'typing') {
        const p = socketData.payload || {};
        const typingUser = p.user || {};
        const uidVal = typingUser.id;
        const is_typing = !!p.is_typing;
        const roomIdNum = normId(p.room_id || currentRid);

        if (!uidVal) return;

        setTypingByRoom((prev) => {
          const curr = prev[roomIdNum] || {};
          const map = new Map(Object.entries(curr));
          const key = String(uidVal);

          if (is_typing) {
            map.set(key, typingUser);
          } else {
            map.delete(key);
          }

          return { ...prev, [roomIdNum]: Object.fromEntries(map) };
        });

        if (is_typing) {
          const key = `${roomIdNum}:${uidVal}`;
          if (typingTimeoutsRef.current[key]) {
            clearTimeout(typingTimeoutsRef.current[key]);
          }
          typingTimeoutsRef.current[key] = setTimeout(() => {
            setTypingByRoom((prev) => {
              const curr = prev[roomIdNum] || {};
              const map = new Map(Object.entries(curr));
              map.delete(String(uidVal));
              const next = { ...prev };
              if (map.size > 0) {
                next[roomIdNum] = Object.fromEntries(map);
              } else {
                delete next[roomIdNum];
              }
              return next;
            });
            delete typingTimeoutsRef.current[key];
          }, 6500);
        } else {
          const key = `${roomIdNum}:${uidVal}`;
          if (typingTimeoutsRef.current[key]) {
            clearTimeout(typingTimeoutsRef.current[key]);
            delete typingTimeoutsRef.current[key];
          }
        }
      }

      if (socketData.type === 'message') {
        const message = handlersRef.current.normalizeMessage(socketData.payload || {});
        const eventType = message.event || 'created';
        const isOwn = message.sender?.id === user?.id;
        const rawRoomId =
          message.room_id ??
          message.roomId ??
          (typeof message.room === 'object' ? message.room?.id : message.room);
        const messageRoomId = normId(rawRoomId != null ? rawRoomId : currentRid);
        const isCurrentRoomOpen = widgetOpenRef.current && normId(currentRoomRef.current) === messageRoomId;
        const roomInfo = roomsRef.current.find((room) => normId(room.id) === messageRoomId);

        const attachments = message.attachments || [];
        const basePreview = (message.content || '').trim();
        let previewText = basePreview || (attachments.length > 1 ? `Вложения (${attachments.length})` : attachments.length === 1 ? 'Вложение' : 'Новое сообщение');
        if (message.is_deleted) previewText = 'Сообщение удалено';
        if (previewText.length > 60) previewText = `${previewText.slice(0, 57)}…`;

        handlersRef.current.setMessagesMap((prev) => {
          const existing = prev[messageRoomId] || [];
          const idx = existing.findIndex((it) => normId(it.id) === normId(message.id));

          if (eventType === 'deleted') {
            if (idx === -1) return prev;
            const updated = [...existing];
            updated[idx] = { ...existing[idx], ...message, is_deleted: true };
            return { ...prev, [messageRoomId]: updated };
          }

          if (eventType === 'updated') {
            const updated = idx === -1
              ? [...existing, message]
              : existing.map((it) => (normId(it.id) === normId(message.id) ? { ...it, ...message } : it));
            return { ...prev, [messageRoomId]: updated };
          }

          if (idx !== -1) {
            const updated = existing.map((it) => (normId(it.id) === normId(message.id) ? { ...it, ...message } : it));
            return { ...prev, [messageRoomId]: updated };
          }
          return { ...prev, [messageRoomId]: [...existing, message] };
        });

        handlersRef.current.setRooms((prev) => {
          if (!prev?.length) return prev;
          return prev.map((room) => {
            if (normId(room.id) !== messageRoomId) return room;
            const currentUnread = Number(room.unread_count || 0);
            const shouldResetUnread = isOwn || isCurrentRoomOpen || eventType === 'deleted';
            let nextUnread = currentUnread;
            if (eventType === 'created') nextUnread = shouldResetUnread ? 0 : currentUnread + 1;
            else if (eventType === 'deleted') nextUnread = shouldResetUnread ? 0 : Math.max(currentUnread - 1, 0);
            else if (eventType === 'updated' && shouldResetUnread) nextUnread = 0;

            return {
              ...room,
              unread_count: nextUnread,
              last_message: {
                id: message.id,
                content: previewText,
                created_at: message.created_at,
                sender: message.sender,
              },
            };
          });
        });

        const roomMuted = handlersRef.current.isRoomMuted(messageRoomId);
        const canPlay = !roomMuted && isSoundEnabledRef.current;

        if (isOwn || isCurrentRoomOpen) {
          handlersRef.current.applyUnreadPatch({ [messageRoomId]: 0 });
          if (isCurrentRoomOpen && eventType === 'created') {
            handlersRef.current.markRoomAsRead(messageRoomId);
          }
          if (!isOwn && eventType === 'created' && canPlay) handlersRef.current.playNotificationSound();
        } else if (eventType === 'created') {
          const curr = Number((prevUnreadByRoomRef.current || {})[normId(messageRoomId)] || 0);
          handlersRef.current.applyUnreadPatch({ [normId(messageRoomId)]: curr + 1 });
          if (canPlay) handlersRef.current.playNotificationSound();
        }

        if (!isOwn && eventType === 'created') {
          if (!isCurrentRoomOpen && shouldDisplayPopup(message.id)) {
            handlersRef.current.enqueuePopupNotification?.({
              id: `${message.id}-${Date.now()}`,
              roomId: messageRoomId,
              roomName: roomInfo?.display_name || roomInfo?.name,
              authorName: message.sender?.full_name || message.sender?.username || 'Сообщение',
              preview: previewText,
              folder: resolveRoomFolder(roomInfo),
            });
          }
          handlersRef.current.showDesktopNotification?.({
            message,
            preview: previewText,
            roomId: messageRoomId,
            title: roomInfo?.display_name || roomInfo?.name,
          });
        }
      }
    } catch (e) {
      console.error('Chat socket parse error', e);
    }
  };

  // useEffect для сокета комнаты
  useEffect(() => {
    const rid = normId(currentRoomId);
    if (!rid) return;
    if (roomSocketRef.current) {
      try { roomSocketRef.current.close(); } catch (e) { console.error('Error closing socket:', e); }
      roomSocketRef.current = null;
    }
    let cancelled = false;
    let reconnectTimeout = null;
    let reconnectAttempt = 0;
    const connect = () => {
      if (cancelled) return;
      const socket = new WebSocket(buildRoomWebsocketUrl(rid));
      roomSocketRef.current = socket;
      socket.onmessage = handleSocketMessage;
      socket.onopen = () => { reconnectAttempt = 0; };
      socket.onclose = () => {
        if (roomSocketRef.current === socket) roomSocketRef.current = null;
        if (cancelled) return;
        reconnectAttempt += 1;
        reconnectTimeout = setTimeout(connect, Math.min(30000, 1000 * (2 ** reconnectAttempt)));
      };
      socket.onerror = () => {};
    };
    connect();

    return () => {
      cancelled = true;
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }

      // Очищаем все таймеры тайпинга при размонтировании
      Object.keys(typingTimeoutsRef.current).forEach(key => {
        if (key.startsWith(`${rid}:`)) {
          clearTimeout(typingTimeoutsRef.current[key]);
          delete typingTimeoutsRef.current[key];
        }
      });

      try {
        roomSocketRef.current?.close(1000, 'room changed');
      } catch (e) {
        console.error('Error closing socket on cleanup:', e);
      }

      roomSocketRef.current = null;
    };
  }, [currentRoomId]);

  // hub-сокет
  useEffect(() => {
    if (!user) {
      hubHasConnectedRef.current = false;
      if (hubSocketRef.current) { try { hubSocketRef.current.close(); } catch {} hubSocketRef.current = null; }
      return;
    }
    if (hubSocketRef.current) { try { hubSocketRef.current.close(); } catch {} hubSocketRef.current = null; }
    if (hubReconnectTimerRef.current) { clearTimeout(hubReconnectTimerRef.current); hubReconnectTimerRef.current = null; }
    hubReconnectAttemptRef.current = 0;
    let cancelled = false;
    let heartbeatTimer = null;

    const stopHeartbeat = () => {
      if (heartbeatTimer) clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    };

    const openHub = () => {
      if (cancelled) return;
      const url = buildHubWebsocketUrl();
      const socket = new WebSocket(url);
      hubSocketRef.current = socket;

      socket.onopen = () => {
        stopHeartbeat();
        heartbeatTimer = setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ action: 'ping' }));
          }
        }, 60000);
        hubReconnectAttemptRef.current = 0;
        const isReconnect = hubHasConnectedRef.current;
        hubHasConnectedRef.current = true;
        // Первичная инициализация уже загружает unread. Не дублируем её при
        // одновременном открытии hub-сокета; после реального reconnect запрос нужен.
        if (isReconnect && isInitialLoadRef.current && !initializationInFlightRef.current) {
          handlersRef.current.refreshUnreadSafe({ force: true });
        }
      };
      socket.onclose = () => {
        stopHeartbeat();
        if (hubSocketRef.current === socket) hubSocketRef.current = null;
        if (cancelled) return;
        const attempt = (hubReconnectAttemptRef.current = hubReconnectAttemptRef.current + 1);
        const delay = Math.min(30000, 500 * Math.pow(2, attempt - 1));
        hubReconnectTimerRef.current = setTimeout(() => openHub(), delay);
      };
      socket.onerror = (e) => { console.warn('Hub socket error', e); };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const type = data?.type;

          // агрегированный патч
          if (type === 'rooms_patch' || type === 'rooms.patch') {
            const patch = (data.payload && data.payload.patch) || data.patch || {};
            const prevMap = prevUnreadByRoomRef.current || {};
            const increases = [];
            let unreadBeep = false;
            Object.entries(patch).forEach(([ridRaw, val]) => {
              const rid = normId(ridRaw);
              const prev = Number(prevMap[rid] || 0);
              const curr = Number(val || 0);
              const isCurrent = rid === normId(currentRoomRef.current);
              const muted = handlersRef.current.isRoomMuted(rid);
              if (curr > prev) {
                if (!isCurrent && !muted) {
                  increases.push(rid);
                  if (!unreadBeep && shouldPlayUnreadSound(rid, curr)) {
                    unreadBeep = true;
                  }
                }
              }
            });
            handlersRef.current.applyUnreadPatch(patch);
            if (unreadBeep && isSoundEnabledRef.current) handlersRef.current.playNotificationSound();
            if (increases.length) {
              increases.slice(0, 3).forEach((rid) => {
                const room = roomsRef.current.find((item) => normId(item.id) === rid);
                handlersRef.current.showDesktopNotification?.({
                  roomId: rid,
                  title: room?.name || 'Новые сообщения',
                  body: room?.name ? `Новые сообщения в «${room.name}»` : 'Новые сообщения в чате',
                  tag: `chat-room-${rid}`,
                });
              });
            }
            return;
          }

          // сырой ивент (оба варианта)
          if (type === 'hub.unread' || type === 'hub_unread' || type === 'unread') {
            const { room_id, unread } = (data.payload || {});
            if (room_id != null) {
              const rid = normId(room_id);
              const prevMap = prevUnreadByRoomRef.current || {};
              const prev = Number(prevMap[rid] || 0);
              const curr = Number(unread || 0);
              const isCurrent = rid === normId(currentRoomRef.current);
              const muted = handlersRef.current.isRoomMuted(rid);
              if (curr > prev && !isCurrent && !muted) {
                if (isSoundEnabledRef.current && shouldPlayUnreadSound(rid, curr)) {
                  handlersRef.current.playNotificationSound();
                }
                const room = roomsRef.current.find((item) => normId(item.id) === rid);
                handlersRef.current.showDesktopNotification?.({
                  roomId: rid,
                  title: room?.name || 'Новые сообщения',
                  body: room?.name ? `Новые сообщения в «${room.name}»` : 'Новые сообщения в чате',
                  tag: `chat-room-${rid}`,
                });
              }
              handlersRef.current.applyUnreadPatch({ [rid]: curr });
            }
            return;
          }

          if (type === 'popup') {
            const payload = data.payload || {};
            const rawMessage = payload.message || {};
            const normalizedMessage = handlersRef.current.normalizeMessage(rawMessage);
            const rid = normId(payload.room_id || normalizedMessage.room || normalizedMessage.room_id);
            if (!rid) return;
            if (normalizedMessage.sender?.id && normalizedMessage.sender.id === user?.id) return;
            const isCurrentRoomOpen = widgetOpenRef.current && normId(currentRoomRef.current) === rid;
            if (isCurrentRoomOpen) return;
            if (!shouldDisplayPopup(normalizedMessage.id)) return;

            const attachments = normalizedMessage.attachments || [];
            const basePreview = (normalizedMessage.content || '').trim();
            let previewText = basePreview || (attachments.length > 1 ? `Вложения (${attachments.length})` : attachments.length === 1 ? 'Вложение' : 'Новое сообщение');
            if (normalizedMessage.is_deleted) previewText = 'Сообщение удалено';
            if (previewText.length > 60) previewText = `${previewText.slice(0, 57)}…`;

            const roomInfo = roomsRef.current.find((room) => normId(room.id) === rid);
            const roomName = roomInfo?.display_name || roomInfo?.name || payload.room_name || 'Чат';
            const folder = roomInfo ? resolveRoomFolder(roomInfo) : (payload.room_folder || 'projects');

            handlersRef.current.enqueuePopupNotification?.({
              id: `${normalizedMessage.id}-${Date.now()}`,
              roomId: rid,
              roomName,
              authorName: normalizedMessage.sender?.full_name || normalizedMessage.sender?.username || 'Сообщение',
              preview: previewText,
              folder,
            });
            return;
          }

          // === обработка события 'typing' ===
          if (data.type === 'typing') {
            const p = data.payload || {};
            const typingUser = p.user || {};
            const uidVal = typingUser.id;
            const is_typing = !!p.is_typing;
            const roomIdNum = normId(p.room_id);
            if (!uidVal) return;
            setTypingByRoom((prev) => {
              const curr = prev[roomIdNum] || {};
              const map = new Map(Object.entries(curr));
              const key = String(uidVal);
              if (is_typing) {
                map.set(key, typingUser);
              } else {
                map.delete(key);
              }
              return { ...prev, [roomIdNum]: Object.fromEntries(map) };
            });
            // авто-стоп тайпинга (аналогично roomSocketRef)
            const key = `${roomIdNum}:${uidVal}`;
            if (typingTimeoutsRef.current[key]) {
              clearTimeout(typingTimeoutsRef.current[key]);
            }
            if (is_typing) {
              typingTimeoutsRef.current[key] = setTimeout(() => {
                setTypingByRoom((prev) => {
                  const curr = prev[roomIdNum] || {};
                  const map = new Map(Object.entries(curr));
                  map.delete(String(uidVal));
                  const next = { ...prev };
                  if (map.size > 0) {
                    next[roomIdNum] = Object.fromEntries(map);
                  } else {
                    delete next[roomIdNum];
                  }
                  return next;
                });
                delete typingTimeoutsRef.current[key];
              }, 6500);
            } else {
              if (typingTimeoutsRef.current[key]) {
                clearTimeout(typingTimeoutsRef.current[key]);
                delete typingTimeoutsRef.current[key];
              }
            }
            return;
          }

          // === обработка событий закрепов через hub-socket ===
          if (data.type === 'pinned' && data.payload) {
            handlersRef.current.applyPinPatch(data.payload);
            return;
          }
          if (data.type === 'unpinned' && data.payload) {
            handlersRef.current.applyPinPatch(data.payload);
            return;
          }

        } catch (e) {
          console.error('Hub socket parse error', e);
        }
      };
    };

    openHub();
    return () => {
      cancelled = true;
      stopHeartbeat();
      if (hubReconnectTimerRef.current) { clearTimeout(hubReconnectTimerRef.current); hubReconnectTimerRef.current = null; }
      if (hubSocketRef.current) { try { hubSocketRef.current.close(); } catch {} hubSocketRef.current = null; }
    };
  }, [user, shouldPlayUnreadSound]);

  useEffect(() => () => {
    if (audioContextRef.current && typeof audioContextRef.current.close === 'function') {
      audioContextRef.current.close().catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const unlock = () => {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      if (!audioContextRef.current) audioContextRef.current = new AudioCtx();
      else if (audioContextRef.current.state === 'suspended') audioContextRef.current.resume().catch(() => {});
    };
    window.addEventListener('click', unlock, { passive: true });
    window.addEventListener('keydown', unlock, { passive: true });
    return () => {
      window.removeEventListener('click', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  const handleSendMessage = useCallback(async (roomId, payload) => {
    if (!roomId) return;
    const rid = normId(roomId);
    try {
      // Останавливаем индикатор тайпинга перед отправкой сообщения
      const socket = roomSocketRef.current;
      if (socket && socket.readyState === WebSocket.OPEN) {
        try {
          socket.send(JSON.stringify({ action: 'typing', payload: { is_typing: false } }));
        } catch {}
      }

      // Сбрасываем состояние тайпинга
      if (typingStateRef.current.timeoutId) {
        clearTimeout(typingStateRef.current.timeoutId);
        typingStateRef.current.timeoutId = null;
      }
      typingStateRef.current.isTyping = false;

      const response = await chatService.sendRoomMessage(rid, payload);
      const message = handlersRef.current.normalizeMessage(response.data);
      handlersRef.current.setMessagesMap((prev) => {
        const existing = prev[rid] || [];
        const exists = existing.some((it) => it.id === message.id);
        if (exists) {
          const replaced = existing.map((it) => (it.id === message.id ? message : it));
          return { ...prev, [rid]: replaced };
        }
        return { ...prev, [rid]: [...existing, message] };
      });
      handlersRef.current.markRoomAsRead(rid);
      return message;
    } catch (error) {
      console.error('Chat: failed to send message', error);
      throw error;
    }
  }, []);

  // Улучшаем обработку тайпинга
  const value = useMemo(() => ({
    rooms,
    roomsLoading,
    currentRoomId,
    setCurrentRoomId,
    messages: messagesMap[normId(currentRoomId)] || [],
    hasMoreMessages: Boolean(hasMoreByRoom[normId(currentRoomId)]),
    messagesLoading,
    loadMoreMessages: () => {
      if (!currentRoomId) return Promise.resolve();
      const rid = normId(currentRoomId);
      if (hasMoreByRoom[rid] === false) return Promise.resolve();
      const messagesForRoom = messagesMap[rid] || [];
      if (!messagesForRoom.length) {
        return handlersRef.current.loadMessages(rid, { append: true });
      }
      const first = messagesForRoom[0];
      return handlersRef.current.loadMessages(rid, { beforeId: first.id, append: true });
    },
    sendMessage: handleSendMessage,
    updateMessage: handleUpdateMessage,
    deleteMessage: handleDeleteMessage,
    widgetOpen,
    setWidgetOpen,
    refreshRooms: loadRooms,
    refreshUnread,
    unreadByRoom,
    totalUnread,
    markRoomAsRead: (rid) => handlersRef.current.markRoomAsRead(rid),
    members: membersMap[normId(currentRoomId)] || [],
    fetchMembers: (rid) => handlersRef.current.fetchMembers(rid),
    toggleRoomMute: handleToggleMute,
    userSettings,
    toggleGlobalSound: handleToggleGlobalSound,
    desktopNotificationsSupported: notificationApiSupported,
    desktopNotificationsEnabled,
    desktopNotificationPermission,
    desktopNotificationRequesting,
    toggleDesktopNotifications,
    notificationSoundUrl,
    isSoundEnabled,
    typingByRoom,
    notifyTypingStart: (rid) => {
      if (!rid || !roomSocketRef.current) return;

      const now = Date.now();
      const lastTyping = typingStateRef.current.lastTypingTime || 0;

      // Отправляем событие тайпинга не чаще чем раз в секунду
      if (!typingStateRef.current.isTyping || (now - lastTyping > 1000)) {
        typingStateRef.current.isTyping = true;
        typingStateRef.current.lastTypingTime = now;

        const socket = roomSocketRef.current;
        if (socket && socket.readyState === WebSocket.OPEN) {
          try {
            socket.send(JSON.stringify({ action: 'typing', payload: { is_typing: true } }));
          } catch (e) {
            console.error('Failed to send typing event:', e);
          }
        }
      }

      // Обновляем таймер автоматической остановки
      if (typingStateRef.current.timeoutId) {
        clearTimeout(typingStateRef.current.timeoutId);
      }

      typingStateRef.current.timeoutId = setTimeout(() => {
        if (!roomSocketRef.current || typingStateRef.current.timeoutId === null) return;

        typingStateRef.current.isTyping = false;
        typingStateRef.current.lastTypingTime = 0;

        const socket = roomSocketRef.current;
        if (socket && socket.readyState === WebSocket.OPEN) {
          try {
            socket.send(JSON.stringify({ action: 'typing', payload: { is_typing: false } }));
          } catch (e) {
            console.error('Failed to send typing stop event:', e);
          }
        }

        typingStateRef.current.timeoutId = null;
      }, 2000);
    },
    notifyTypingStopNow: (rid) => {
      if (!rid || !roomSocketRef.current) return;

      // Немедленно останавливаем таймер
      if (typingStateRef.current.timeoutId) {
        clearTimeout(typingStateRef.current.timeoutId);
        typingStateRef.current.timeoutId = null;
      }

      // Сбрасываем состояние, только если оно было активно
      if (typingStateRef.current.isTyping) {
        typingStateRef.current.isTyping = false;
        typingStateRef.current.lastTypingTime = 0;

        const socket = roomSocketRef.current;
        if (socket && socket.readyState === WebSocket.OPEN) {
          try {
            socket.send(JSON.stringify({ action: 'typing', payload: { is_typing: false } }));
          } catch (e) {
            console.error('Failed to send immediate typing stop event:', e);
          }
        }
      }
    },
    pinMessage,
    unpinMessage,
    addReaction,
    removeReaction,
    searchUsers: searchDirectoryUsers,
    startDirectChat: startDirectConversation,
    popupNotifications,
    dismissPopupNotification,
  }), [
    rooms, roomsLoading, currentRoomId, messagesMap, messagesLoading,
    handleSendMessage, handleUpdateMessage, handleDeleteMessage,
    widgetOpen, loadRooms, userSettings, handleToggleMute,
    handleToggleGlobalSound, notificationSoundUrl, isSoundEnabled,
    unreadByRoom, totalUnread, membersMap, refreshUnread,
    typingByRoom, desktopNotificationsEnabled, desktopNotificationPermission,
    desktopNotificationRequesting,
    toggleDesktopNotifications, searchDirectoryUsers, startDirectConversation,
    popupNotifications, dismissPopupNotification,
    hasMoreByRoom,
  ]);

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) throw new Error('useChat must be used within ChatProvider');
  return context;
};
